import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, InputNumber, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { TextArea } = Input;
const { Text } = Typography;

interface DisputeAmountSheetProps {
  open: boolean;
  titleKey: string;
  subtitleKey?: string;
  /** Omit to hide the amount field entirely -- that's the "just tell your side" case. */
  amountLabelKey?: string;
  textPlaceholderKey: string;
  confirmKey: string;
  /** Pre-fills the amount field, e.g. countering the offer already on the table. */
  initialAmount?: number;
  requireText?: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (values: { amount?: number; text: string }) => void;
}

/**
 * The one input surface every step of a dispute uses: an amount, some
 * words, or both. Opening a claim, answering one, and countering an offer
 * are the same gesture with different labels, so they are the same
 * component -- three near-identical sheets would drift apart the first time
 * anyone touched one of them.
 *
 * Same desktop-Modal / mobile-bottom-sheet split as
 * HandoverAcknowledgementSheet and HostCancelBookingModal -- see the
 * latter's own doc comment for why a sheet beats a small centred dialog on
 * a phone, and why the drag handle is the only draggable part (there's a
 * TextArea in here to tap into).
 */
const DisputeAmountSheet: React.FC<DisputeAmountSheetProps> = ({
  open,
  titleKey,
  subtitleKey,
  amountLabelKey,
  textPlaceholderKey,
  confirmKey,
  initialAmount,
  requireText = true,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [amount, setAmount] = useState<number | null>(initialAmount ?? null);
  const [text, setText] = useState('');
  const dragControls = useDragControls();

  const handleClose = () => {
    setAmount(initialAmount ?? null);
    setText('');
    onClose();
  };

  const handleSubmit = () => {
    if (requireText && !text.trim()) {
      message.error(translate(t, TRANSLATIONS.DISPUTE_OPEN_REASON_REQUIRED));
      return;
    }
    if (amountLabelKey && (amount == null || amount <= 0)) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED));
      return;
    }
    onSubmit({ amount: amountLabelKey ? amount ?? undefined : undefined, text: text.trim() });
  };

  const body = (
    <>
      {amountLabelKey && (
        <div style={{ marginBottom: 14 }}>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {translate(t, amountLabelKey)}
          </Text>
          <InputNumber
            min={0}
            step={10}
            value={amount}
            onChange={setAmount}
            addonAfter="€"
            inputMode="decimal"
            size="large"
            style={{ width: '100%' }}
            disabled={submitting}
          />
        </div>
      )}
      <TextArea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={translate(t, textPlaceholderKey)}
        maxLength={2000}
        disabled={submitting}
        style={{ borderRadius: 16 }}
      />
    </>
  );

  const confirmButton = (
    <Button block type="primary" size="large" loading={submitting} onClick={handleSubmit} style={{ borderRadius: 999 }}>
      {translate(t, confirmKey)}
    </Button>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="dispute-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="dispute-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
                maxHeight: '88vh',
                overflowY: 'auto',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <Text strong style={{ display: 'block', fontSize: 16 }}>
                {translate(t, titleKey)}
              </Text>
              {subtitleKey && (
                <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
                  {translate(t, subtitleKey)}
                </Text>
              )}
              <div style={{ marginTop: subtitleKey ? 0 : 14 }}>{body}</div>
              <div style={{ marginTop: 16 }}>{confirmButton}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal title={translate(t, titleKey)} open={open} onCancel={handleClose} footer={confirmButton} destroyOnClose>
      {subtitleKey && (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
          {translate(t, subtitleKey)}
        </Text>
      )}
      {body}
    </Modal>
  );
};

export default DisputeAmountSheet;
